import { Component, Inject, OnInit } from '@angular/core';
import { DataTableHeaderOption } from '@dxo/ng-swift-ui';
import { BillingData, BillingListSearchParameters, CellItemType } from '../billing-page.types';
import { BillingService } from '../billing.service';
import {
  BillingsClient,
  BillingSearchRequest,
  BillingSearchResponse,
  SearchSortDirection,
  SearchSortField
} from '@benefitsui/app/api/benefits-clients';
import { BehaviorSubject, combineLatest, merge } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { BillingPageComponent } from '../billing-page.component';
import { DropdownOption } from '@benefitsui/app/lib/common.types';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { Constants } from '@benefitsui/app/pages/billing-page/billing-page.types';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { formatActiveEnvironmentDocumentLink } from '@benefitsui/app/shared/activeenvironment/activeenvironment';
import { DateTimeUtilities } from '@benefitsui/app/shared/utilities/dateTimeUtilities';

@Component({
  selector: 'app-billing-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: false,
  providers: [UserContextService]
})
export class BillingListComponent implements OnInit {
  // :  Constants
  ActionsText = 'Actions';
  BillGroupText = 'Bill group';
  InvoicePeriodText = 'Invoice period';
  InvoicePeriodAndNumberText = Constants.InvoicePeriodAndNumberText;
  RunDateText = 'Run date';
  SortKey = 'sort';

  // :  Data members
  formGroup: FormGroup;

  tableHeaders: DataTableHeaderOption[];
  userContext: UserContext;
  userType: string;
  invoicePeriodOptions: DropdownOption<string>[] = [];
  isEmployee: boolean = false;
  billGroupNumberOptions: DropdownOption<string>[] = [];
  StatusOptions: DropdownOption<string>[] = [
    { key: 'Paid', displayName: 'Paid' },
    { key: 'Unpaid', displayName: 'Unpaid' }
  ];

  SortDirection = SearchSortDirection;
  private pageSize$ = new BehaviorSubject<number>(10);
  private page$ = new BehaviorSubject<number>(1);
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Descending);
  invoicePeriodString$ = new BehaviorSubject<string | undefined>(undefined);
  status$ = new BehaviorSubject<string | null>(Defaults.stringEmpty);
  billGroupNumber$ = new BehaviorSubject<string | null>(Defaults.stringEmpty);
  sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.Invoice);

  billings$ = combineLatest([
    this.page$,
    this.pageSize$.pipe(distinctUntilChanged()),
    this.sortDirection$.pipe(distinctUntilChanged()),
    this.sortField$.pipe(distinctUntilChanged()),
    this.status$.pipe(distinctUntilChanged()),
    this.invoicePeriodString$.pipe(distinctUntilChanged())
  ])
    .pipe(
      switchMap((vals) => {
        this.searchParameter.page = vals[0];
        this.searchParameter.pageSize = vals[1];
        this.searchParameter.sortDirection = vals[2];
        this.searchParameter.sortField = vals[3];
        this.searchParameter.status = vals[4] as string;
        this.searchParameter.invoicePeriodString = vals[5] as string;
        this.searchParameter.billGroupNumber = this.billGroupNumber$?.value ?? Defaults.stringEmpty;

        const criteria: BillingSearchRequest = {
          billGroupNumber: this.searchParameter.billGroupNumber ? this.searchParameter.billGroupNumber : undefined,
          invoiceNumber: undefined,
          invoicePeriod: undefined,
          invoicePeriodString: this.searchParameter.invoicePeriodString,
          page: this.searchParameter.page,
          pageSize: this.searchParameter.pageSize,
          policyId: undefined,
          runDateFrom: undefined,
          runDateTo: undefined,
          sortDirection: this.searchParameter.sortDirection,
          sortField: this.searchParameter.sortField,
          status: this.searchParameter.status ? this.searchParameter.status : undefined,
          clearCache: this.billingService.clearCache,
          refreshCache: this.billingService.refreshCache
        };

        const response = this.billingsClient.search(criteria).pipe(map((x) => x.data));

        return response;
      })
    )
    .pipe(
      map((x) => this.transformData(x)),
      tap(() => (this.isLoading = false))
    );

  isLoading = true;
  tableCells!: CellItemType[];
  hasServerError = false;

  searchParameter: BillingListSearchParameters = {
    status: Defaults.stringEmpty,
    billGroupNumber: Defaults.stringEmpty,
    invoicePeriod: undefined,
    invoicePeriodString: undefined,
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.Invoice,
    sortDirection: SearchSortDirection.Descending
  };

  defaultTableHeaders: DataTableHeaderOption[] = [
    { text: '', key: 'checkbox', width: '0%' },
    { text: this.InvoicePeriodAndNumberText, key: 'invoice', width: '20%' },
    { text: this.BillGroupText, key: 'billGroup', width: '17%' },
    { text: this.ActionsText, key: 'actions', width: '10%' },
    { text: Defaults.stringEmpty, key: 'downloads', width: '10%' }
  ];

  employeeTableHeaders: DataTableHeaderOption[] = [
    { text: this.RunDateText, key: 'runDate', width: '15%' },
    { text: this.InvoicePeriodAndNumberText, key: 'invoice', width: '20%' },
    { text: this.BillGroupText, key: 'billGroup', width: '17%' },
    { text: this.ActionsText, key: 'actions', width: '10%' },
    { text: Defaults.stringEmpty, key: 'downloads', width: '10%' }
  ];

  // :  Constructor
  constructor(
    private billingsClient: BillingsClient,
    private billingService: BillingService,
    private userContextService: UserContextService,
    private fb: UntypedFormBuilder,
    @Inject(BillingPageComponent) private parent: BillingPageComponent
  ) {
    this.fb = fb;
    this.userContext = this.userContextService.get();
    this.userType = this.userContext.symClass;
    this.formGroup = this.fb.group({
      invoicePeriod: Defaults.stringEmpty,
      status: Defaults.stringEmpty,
      billGroupNumber: Defaults.stringEmpty
    });

    switch (this.userContext.symClass.toLowerCase()) {
      case 'employee':
        this.isEmployee = true;
        this.tableHeaders = this.employeeTableHeaders;
        break;
      case 'distributor':
        this.tableHeaders = this.defaultTableHeaders;
        break;
      case 'sponsor':
        this.tableHeaders = this.defaultTableHeaders;
        break;
      default:
        this.tableHeaders = this.defaultTableHeaders;
        break;
    }
  }

  ngOnInit(): void {
    if (this.formGroup) {
      const invoicePeriod = this.formGroup.get('invoicePeriod');
      const billGroupNumber = this.formGroup.get('billGroupNumber');
      const status = this.formGroup.get('status');

      invoicePeriod!.valueChanges.subscribe((value) => {
        this.invoicePeriodString$.next(value);
      });
      status!.valueChanges.subscribe((value) => {
        this.status$.next(value);
      });
      billGroupNumber!.valueChanges.subscribe((value) => {
        this.billGroupNumber$.next(value);
      });
    }

    merge(this.billGroupNumber$, this.invoicePeriodString$).subscribe(() => this.page$.next(1));
  }

  transformData(response: BillingSearchResponse | undefined): BillingData {
    const returnTableCells: CellItemType[] = [];

    response?.billings?.map((billing) => {
      const runDateString = DateTimeUtilities.symetraDateTimeToString(billing.runDate);        
      const invoicePeriodString = DateTimeUtilities.billingPeriodToString(billing.invoicePeriod);

      const cellItemType = {
        actions: this.billingService.getAction(
          runDateString ?? Defaults.stringEmpty,
          billing.premiumDetailsId ?? Defaults.stringEmpty,
          billing.billingType ?? Defaults.stringEmpty
        ),
        amount: billing.amount ?? 0,
        amountUI: this.billingService.formatAmount(billing.amountUI ?? Defaults.stringEmpty),
        billGroupName: billing.billGroupName ?? Defaults.stringEmpty,
        billGroupNumber: billing.billGroupNumber ?? Defaults.stringEmpty,
        indicator: this.getStatusColor(billing.status ?? Defaults.stringEmpty),
        invoice: invoicePeriodString,
        invoiceKey: billing.invoiceKey ?? Defaults.stringEmpty,
        premiumKey: billing.premiumKey ?? Defaults.stringEmpty,
        dueDateKey: billing.dueDatekey ?? Defaults.stringEmpty,
        startDate: billing.coverageStartDate ?? Defaults.stringEmpty,
        endDate: billing.coverageStopDate ?? Defaults.stringEmpty,
        invoiceNumber: billing.invoiceNumber ?? Defaults.stringEmpty,
        invoicePeriod: {
          from: billing.invoicePeriod?.from ?? DateTimeUtilities.symetraDateTimeDefault,
          to: billing.invoicePeriod?.to ?? DateTimeUtilities.symetraDateTimeDefault
        },
        invoicePeriodString: invoicePeriodString,
        pdfUrl: billing.fileUri ?? Defaults.stringEmpty,
        premiumCalc: billing.amountUI,
        premiumID: billing.premiumDetailsId,
        runDateString: runDateString,
        selected: false,
        status: billing.status ?? Defaults.stringEmpty,
        xlsxUrl: billing.excelFileUri ?? Defaults.stringEmpty,
        submittedBy: billing.submittedBy ?? Defaults.stringEmpty,
        submittedDate: billing.submittedDate ?? Defaults.stringEmpty,
        saComment: billing.saComment ?? Defaults.stringEmpty
      } as CellItemType;

      this.billingService.setClearCache(false);
      this.billingService.setRefreshCache(false);
      returnTableCells.push(cellItemType);
    });

    this.invoicePeriodOptions= (response?.billingPeriodList ?? [])
      .map((billingPeriod) =>  ({
        key: billingPeriod,
        displayName: billingPeriod
      }));
          
    this.billGroupNumberOptions = (response?.billingGroupList ?? []).map((billingGroupList) => ({
      key: billingGroupList.split('\n')[1].trim(),
      displayName: billingGroupList
    }));

    this.tableCells = returnTableCells ?? [];
    return {
      listBilling: returnTableCells ?? [],
      totalResults: response?.totalResults ?? 0,
      invoicePeriodList: response?.billingPeriodList ?? []
    };
  }
 
  formatDocumentLink(item: string): string {
    return formatActiveEnvironmentDocumentLink(item);
 }

  goPayment() {
    this.parent.goPayment(this.selectedItems);
  }

  appendMoreResults() {
    console.log('append');
  }

  async submit() {
    console.log('Submitted');
  }

  onClickTableHeader(sortField: string) {
    let newSortDirection = SearchSortDirection.Descending;
    if (this.searchParameter.sortDirection === SearchSortDirection.Descending) {
      newSortDirection = SearchSortDirection.Ascending;
    }
    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];

    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  onFilterChange(value: string, filterType: string) {
    if (filterType === 'invoice') {
      this.invoicePeriodString$.next(value);
    } else if (filterType === 'status') {
      this.status$.next(value);
    } else if (filterType === 'group') {
      this.billGroupNumber$.next(value);
    }
  }

  clearFilters() {
    this.formGroup.setValue({
      invoicePeriod: Defaults.stringEmpty,
      status: Defaults.stringEmpty,
      billGroupNumber: Defaults.stringEmpty
    });
  }

  clearCache() {
    this.billingService.setClearCache(true);
    this.ngOnInit()
  }
  
  onPageChange(page: number) {
    this.page$.next(page);
    this.parent.scrollToTop();
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  getStatusColor(status: string | undefined) {
    switch (status) {
      case 'Unpaid':
        return '#FDD835';
      case 'Paid':
        return '#8ABF2E';
      default:
        return Defaults.stringEmpty;
    }
  }

  selectedItems: CellItemType[] = [];

  allSelected = false;

  //selects or deselects all checkboxes
  selectAll(input: boolean) {
    this.allSelected = input;
    this.selectedItems = input ? this.tableCells : [];
    if (input) {
      this.tableCells.forEach((cell) => (cell.selected = true));
    } else {
      this.tableCells.forEach((cell) => (cell.selected = false));
    }
  }

  //selects or deselects individual checkboxes
  selectItem(item: CellItemType, selected: boolean) {
    if (this.allSelected) {
      this.allSelected = false;
    }

    item.selected = selected;
    if (selected) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = this.selectedItems.filter((listItem) => listItem.invoiceNumber !== item.invoiceNumber);
    }
  }

  goPremium(item: CellItemType) {
    this.parent.goPremium(item);
  }
}
